import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, RefreshCw, UserPlus, X, Shield, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DispatchersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ email: '', password: '', name: '', phone: '', role: 'dispatcher' });
  const [saving, setSaving] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/auth/users`);
      setUsers(res.data);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const resetForm = () => {
    setForm({ email: '', password: '', name: '', phone: '', role: 'dispatcher' });
    setEditingUser(null);
    setShowForm(false);
  };

  const handleEdit = (user) => {
    setForm({ email: user.email, password: '', name: user.name, phone: user.phone || '', role: user.role });
    setEditingUser(user);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingUser) {
        const payload = { name: form.name, phone: form.phone || null, role: form.role };
        if (form.password.trim()) payload.password = form.password;
        await axios.put(`${API}/auth/users/${editingUser.id}`, payload);
        toast.success('User updated');
      } else {
        if (!form.password.trim()) { toast.error('Password is required'); setSaving(false); return; }
        await axios.post(`${API}/auth/users`, form);
        toast.success('Dispatcher created');
      }
      resetForm();
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete ${user.name || user.email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/auth/users/${user.id}`);
      toast.success('User deleted');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  return (
    <div data-testid="dispatchers-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-black text-zinc-900">Local Dispatchers</h2>
          <p className="text-sm text-zinc-500 mt-1">
            Manage dispatcher accounts that log in directly (without TMS)
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchUsers}
            className="flex items-center gap-2 px-3 py-2 text-sm text-zinc-600 hover:text-zinc-900 border border-zinc-200 hover:border-zinc-300 transition-colors"
            data-testid="refresh-users-btn"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm font-bold uppercase tracking-wide transition-colors"
            data-testid="add-dispatcher-btn"
          >
            <UserPlus className="w-4 h-4" />
            Add Dispatcher
          </button>
        </div>
      </div>

      {showForm && (
        <div className="bg-white border border-zinc-200 p-6 mb-6" data-testid="dispatcher-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-zinc-900">
              {editingUser ? 'Edit Dispatcher' : 'New Dispatcher'}
            </h3>
            <button onClick={resetForm} className="text-zinc-400 hover:text-zinc-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-zinc-700 mb-1">Email</label>
              <input
                type="email"
                required
                disabled={!!editingUser}
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-red-500 disabled:bg-zinc-100 disabled:text-zinc-500"
                placeholder="dispatcher@redballxpress.us"
                data-testid="dispatcher-email-input"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-zinc-700 mb-1">
                Password {editingUser && <span className="font-normal text-zinc-400">(leave blank to keep)</span>}
              </label>
              <input
                type="password"
                required={!editingUser}
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-red-500"
                placeholder={editingUser ? 'Leave blank to keep current' : 'Min 8 chars, upper+lower+digit'}
                data-testid="dispatcher-password-input"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-zinc-700 mb-1">Full Name</label>
              <input
                type="text"
                required
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-red-500"
                placeholder="John Doe"
                data-testid="dispatcher-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-zinc-700 mb-1">Phone</label>
              <input
                type="text"
                value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-red-500"
                placeholder="555-0101"
                data-testid="dispatcher-phone-input"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-zinc-700 mb-1">Role</label>
              <select
                value={form.role}
                onChange={e => setForm({ ...form, role: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-red-500"
                data-testid="dispatcher-role-select"
              >
                <option value="dispatcher">Dispatcher</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                type="submit"
                disabled={saving}
                className="bg-zinc-900 hover:bg-zinc-800 text-white px-6 py-2 text-sm font-bold uppercase tracking-wide transition-colors disabled:opacity-50"
                data-testid="save-dispatcher-btn"
              >
                {saving ? 'Saving...' : editingUser ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-8 h-8 animate-spin text-zinc-400" />
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-20 text-zinc-400">
          <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="font-medium">No local dispatchers yet</p>
          <p className="text-sm mt-1">Click "Add Dispatcher" to create accounts</p>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 overflow-hidden">
          <table className="w-full text-sm" data-testid="dispatchers-table">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-wide text-zinc-500 text-xs">Name</th>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-wide text-zinc-500 text-xs">Email</th>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-wide text-zinc-500 text-xs">Phone</th>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-wide text-zinc-500 text-xs">Role</th>
                <th className="text-right px-4 py-3 font-bold uppercase tracking-wide text-zinc-500 text-xs">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 font-medium text-zinc-900">{u.name}</td>
                  <td className="px-4 py-3 text-zinc-600">{u.email}</td>
                  <td className="px-4 py-3 text-zinc-500">{u.phone || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold uppercase rounded ${
                      u.role === 'admin' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {u.role === 'admin' ? <Shield className="w-3 h-3" /> : <User className="w-3 h-3" />}
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleEdit(u)}
                      className="text-zinc-400 hover:text-zinc-700 p-1 mr-1"
                      data-testid={`edit-user-${u.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(u)}
                      className="text-zinc-400 hover:text-red-600 p-1"
                      data-testid={`delete-user-${u.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
